import { useMemo, useState } from 'react';
import { Pagination, Tag } from '@carbon/react';

import {
  DataTable,
  type DataTableColumnDef,
  type TableState,
} from 'components/data-table';

import type { NamedOwnershipChangeRow } from '../ownership-changes-utils';
import type { AddedOwnershipChangeRow } from '../types';
import styles from './ownership-changes-modal.module.scss';

/**
 * The review's page sizes. It pages in the browser, each list arriving whole, and
 * hands the table one page: the table's own select-all would tick every page.
 */
const PAGE_SIZES = [25, 50, 100];

const TEXT = { type: 'text' as const };

/** A row of either list, named; only an Asset Added says whether it is in the table. */
type ReviewRow = NamedOwnershipChangeRow &
  Partial<Pick<AddedOwnershipChangeRow, 'included'>>;

/** A row as the table holds it: an Asset Added already in the table cannot be ticked. */
type TableRow = ReviewRow & Record<string, unknown> & { _canSelect: boolean };

/**
 * The table's columns, the last headed `propertiesHeader`.
 *
 * @param {string} propertiesHeader - "Properties" or "Properties Excluded"
 * @return {DataTableColumnDef[]} The columns
 */
const columnsFor = (
  propertiesHeader: string
): DataTableColumnDef<TableRow>[] => [
  {
    id: 'name',
    header: 'Asset Name',
    accessorFn: (row) => row.name ?? row.salesforceId,
    meta: TEXT,
    cell: ({ row }) => (
      <span className={styles.assetName}>
        {row.original.name ?? row.original.salesforceId}
        {row.original.included === true && (
          <Tag size="sm" type="green" className={styles.addedTag}>
            Added
          </Tag>
        )}
      </span>
    ),
  },
  {
    id: 'entity',
    header: 'Entity',
    accessorKey: 'entityNames',
    meta: TEXT,
  },
  {
    id: 'packages',
    header: 'Belongs to Package',
    accessorKey: 'packageNames',
    meta: TEXT,
  },
  {
    id: 'propertyCount',
    accessorKey: 'propertyCount',
    header: propertiesHeader,
    meta: { type: 'integer', align: 'center' },
  },
];

const ADDED_COLUMNS = columnsFor('Properties');
const REMOVED_COLUMNS = columnsFor('Properties Excluded');

/** The review's selection of Assets Added: ids, kept across pages. */
export interface OwnershipChangesSelection {
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
  /** Holds the ticks while a change is on its way. */
  disabled: boolean;
}

export interface OwnershipChangesTableProps {
  rows: ReviewRow[];
  /**
   * Assets Added: the rows not yet in the table can be ticked, and those in it
   * show ticked, held and tagged "Added". Absent for the read-only Assets Removed.
   */
  selection?: OwnershipChangesSelection;
}

/**
 * One list of the ownership review, a page at a time in the browser (100, or
 * 25 or 50). With a selection, a tick keeps the ticks of other pages, and the
 * header's tick takes in or leaves out only this page's rows that can be ticked.
 *
 * @param {OwnershipChangesTableProps} props - The rows and, for Assets Added, the selection
 * @return {JSX.Element} The table and its pages
 */
export function OwnershipChangesTable({
  rows,
  selection,
}: OwnershipChangesTableProps) {
  const [paging, setPaging] = useState({ page: 1, pageSize: 100 });
  const { pageSize } = paging;
  // A re-read can shorten the list under the page shown.
  const page = Math.min(
    paging.page,
    Math.max(1, Math.ceil(rows.length / pageSize))
  );

  const pageRows = useMemo<TableRow[]>(
    () =>
      rows
        .slice((page - 1) * pageSize, page * pageSize)
        .map((row) => ({ ...row, _canSelect: row.included === false })),
    [rows, page, pageSize]
  );
  const pageTickable = useMemo(
    () =>
      new Set(
        pageRows.filter((row) => row._canSelect).map((row) => row.salesforceId)
      ),
    [pageRows]
  );
  // Rows already in the table show ticked beside the user's own ticks.
  const ticked = useMemo(
    () => ({
      selectAll: false,
      toggledNodes: [
        ...pageRows
          .filter((row) => row.included === true)
          .map((row) => row.salesforceId),
        ...(selection?.selectedIds ?? []),
      ],
    }),
    [pageRows, selection?.selectedIds]
  );

  /**
   * Takes this page's ticks from the table; the other pages' stay as they were.
   *
   * @param {TableState} state - The table's state after a tick
   */
  const handleSelectionChange = ({
    selection: { toggledNodes },
  }: TableState) => {
    if (!selection) return;
    const shown = new Set(toggledNodes);
    const next = new Set(
      [...selection.selectedIds].filter((id) => !pageTickable.has(id))
    );
    pageTickable.forEach((id) => {
      if (shown.has(id)) next.add(id);
    });
    selection.onSelectedIdsChange(next);
  };

  return (
    <>
      <DataTable<TableRow>
        data={pageRows}
        columns={selection ? ADDED_COLUMNS : REMOVED_COLUMNS}
        getRowId={(row) => row.salesforceId}
        {...(selection && {
          controlledState: { selection: ticked },
          onSelectionChange: handleSelectionChange,
          disableSelection: selection.disabled,
        })}
        config={{
          ...(selection && { selectionType: 'multi' as const }),
          // Nothing to customise, so no toolbar either.
          sorting: false,
          columnResizing: false,
          globalSearch: false,
          columnFilters: false,
          columnVisibility: false,
          columnPinning: false,
          columnReordering: false,
          hideFooter: true,
        }}
        // The body scrolls past this; a short list leaves no empty box.
        maxBodyHeight="24rem"
      />
      <Pagination
        className={styles.pagination}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZES}
        totalItems={rows.length}
        onChange={(next: { page: number; pageSize: number }) =>
          setPaging({
            // A new page size starts again from the first page.
            page: next.pageSize === pageSize ? next.page : 1,
            pageSize: next.pageSize,
          })
        }
      />
    </>
  );
}
